import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import { Provider } from "react-redux";
import { store } from "./store";
import "./index.css";
import { router } from "./routers/MainRouter";
import { RouterProvider } from "react-router-dom";
import "FMSLogger";
import { asyncWithLDProvider } from "launchdarkly-react-client-sdk";
import { setSentryUserFromStorage } from "./utils/sentryUtils";

const SENTRY_URL = window._env_.REACT_APP_SENTRY_URL;
const LAUNCHDARKLY_CLIENT_ID = window._env_.REACT_APP_LAUNCHDARKLY_CLIENT_ID;

if (SENTRY_URL) {
  Sentry.init({
    dsn: SENTRY_URL,
    _experiments: { enableLogs: true },
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.httpClientIntegration(),
      Sentry.launchDarklyIntegration(),
      Sentry.replayIntegration({
        maskAllText:
          window._env_.REACT_APP_SENTRY_REPLAYS_MASK_ALL_TEXT || false,
        blockAllMedia:
          window._env_.REACT_APP_SENTRY_REPLAYS_BLOCK_ALL_MEDIA || false,
      }),
    ],
    tracesSampleRate: 1.0,
    environment: window._env_.REACT_APP_ENV || "development",
    release: window._env_.APP_VERSION_LONG || "",
    replaysSessionSampleRate:
      window._env_.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || 0.1,
    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 1.0,
  });

  // Initialize Sentry user context if user is already logged in
  if (window.localStorage.getItem("JWT")) {
    setSentryUserFromStorage();
  }
}

const App = () => (
  <Provider store={store}>
    <RouterProvider router={router} />
  </Provider>
);

(async () => {
  const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement
  );

  if (!LAUNCHDARKLY_CLIENT_ID) {
    console.warn(
      "LaunchDarkly client ID is not configured. Feature flags will be disabled."
    );
    root.render(<App />);
    return;
  }

  try {
    const LDProvider = await asyncWithLDProvider({
      clientSideID: LAUNCHDARKLY_CLIENT_ID,
      options: {
        inspectors: [Sentry.buildLaunchDarklyFlagUsedHandler()],
      },
    });

    root.render(
      <LDProvider>
        <App />
      </LDProvider>
    );
  } catch (error) {
    console.error("Failed to initialize LaunchDarkly:", error);
    root.render(<App />);
  }
})();
